import { forwardRef, useState } from 'react';
import clsx from 'clsx';

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5">
    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path
      d="M10.6 5.2A10.9 10.9 0 0112 5c6.5 0 10 7 10 7a13.5 13.5 0 01-3.4 4.2M6.6 6.6C4 8.3 2 12 2 12s3.5 7 10 7a10 10 0 004.4-1"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9.9 9.9a3 3 0 004.2 4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Password field with a show/hide toggle. Same look and prop contract as
 * <Input/> (label, error, helperText, react-hook-form register spread) —
 * just renders an eye icon button inside the field.
 */
const PasswordInput = forwardRef(function PasswordInput({ label, error, helperText, className, id, ...props }, ref) {
  const [visible, setVisible] = useState(false);
  const inputId = id || props.name;

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={inputId} className="block text-sm font-medium text-foreground-muted mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          type={visible ? 'text' : 'password'}
          aria-invalid={!!error}
          aria-describedby={error ? `${inputId}-error` : undefined}
          className={clsx(
            'w-full rounded-xl bg-background-elevated border px-3.5 py-2.5 pr-11 text-sm text-foreground',
            'placeholder:text-foreground-subtle transition-colors',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
            error ? 'border-danger' : 'border-border focus:border-border-strong',
            className
          )}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className="absolute inset-y-0 right-0 flex items-center px-3 text-foreground-subtle hover:text-foreground transition-colors"
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
      {error && (
        <p id={`${inputId}-error`} className="mt-1.5 text-xs text-danger">
          {error}
        </p>
      )}
      {!error && helperText && <p className="mt-1.5 text-xs text-foreground-subtle">{helperText}</p>}
    </div>
  );
});

export default PasswordInput;
